import { useState, useMemo, useEffect } from "react";
import { useLoadScript } from "@react-google-maps/api";
import { PageLayout } from "../../../components";
import { TabButton } from "@components";
import { useTranslation } from "react-i18next";
import { OrderStatus } from "../../../types/order/IOrder";
import { useDrivers, useFilteredOrders } from "../../../hooks";
import { Customer, Order } from "../../../models";
import { mapOrderRouteToEnhanced } from "../../../helpers/routeMappers";
import { pageTabBar } from "@design-system";
import { useSearchParams } from "react-router-dom";
import { OrdersView } from "./components/OrdersView";
import { DriverRoutesView } from "./components/DriverRoutesView";
import { IMapRoute, libraries } from "./types";
import { DriverMapSkeleton } from "./components/DriverMapSkeleton";

// Stable status list (module scope) so useFilteredOrders' memo doesn't bust
// on every render.
const MAP_ORDER_STATUSES = [
  OrderStatus.Scheduled,
  OrderStatus.New,
  OrderStatus.InProgress,
  OrderStatus.Completed,
];

export const DriverMapPage = function () {
  const { t } = useTranslation(["common", "map"]);
  const { isLoaded, loadError } = useLoadScript({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const dateFromUrl = searchParams.get("date");
  const orderIdFromUrl = searchParams.get("orderId");

  // Set initial tab based on whether orderId is present
  const [activeTab, setActiveTab] = useState<"orders" | "routes">(
    orderIdFromUrl ? "orders" : "routes",
  );
  const [originalOrders, setOriginalOrders] = useState<IMapRoute[]>([]);
  const [filteredOrders, setfilteredOrders] = useState<IMapRoute[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(
    dateFromUrl ? new Date(dateFromUrl) : new Date(),
  );

  // Realtime orders come from the shared workspace-wide listener; the map only
  // needs the plannable/active statuses, filtered client-side.
  const query = useFilteredOrders(MAP_ORDER_STATUSES);
  const driverQuery = useDrivers(undefined, true);
  const orders = useMemo(() => query.data ?? [], [query.data]) as Order[];
  // Depend on `driverQuery.data`, not the whole `driverQuery` object: react-query
  // returns a new result object every render, so `[driverQuery]` made this memo
  // recompute each render and hand back a brand-new `[]` while data was undefined
  // (e.g. the drivers API hasn't resolved). That new array identity retriggered
  // the [orders, drivers] effect below on every render → infinite update loop.
  const drivers = useMemo(() => driverQuery.data ?? [], [driverQuery.data]);

  // Switch to orders tab when orderId is present in URL
  useEffect(() => {
    if (orderIdFromUrl && activeTab !== "orders") {
      setActiveTab("orders");
    }
  }, [orderIdFromUrl, activeTab]);

  // Håndter bytte av fane
  const handleTabChange = (tab: "orders" | "routes") => {
    setActiveTab(tab);
    // Nullstill ordreId i URL når vi bytter til ordre-fanen
    // Dette vil trigge automatisk valg av første ordre i OrdersView
    if (tab === "orders") {
      setSearchParams({});
    }
  };

  useEffect(() => {
    let filtered = [...originalOrders];

    if (selectedTypes.length > 0) {
      filtered = filtered.filter((order) =>
        selectedTypes.includes(order.status),
      );
    }

    setfilteredOrders(filtered);
  }, [selectedTypes, originalOrders]);

  useEffect(() => {
    const mappedOrders = orders
      .filter((x) => x.status !== OrderStatus.Cancelled)
      .map((x) => ({
        customerName: Customer.getCustomerName(x.customer),
        customer: x.customer,
        id: x.id,
        orderNumber: x.orderNumber,
        type: "Pickup",
        status: x.status,
        driverId: x.driverId,
        driver: drivers.find((d) => d.email === x.driverId),
        route: mapOrderRouteToEnhanced(
          x,
          Customer.getCustomerName(x.customer),
        ),
      }));
    setOriginalOrders(mappedOrders || []);
    setfilteredOrders(mappedOrders || []);
  }, [orders, drivers]);

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <DriverMapSkeleton />;

  return (
    <PageLayout>
      <div>
        {/* Tabs and Filters */}
        <div className={pageTabBar}>
          <div className="px-4">
            <ul className="flex flex-wrap -mb-px text-sm font-medium text-center items-stretch">
              <TabButton
                label={t("map:tabs.orders")}
                isSelected={activeTab === "orders"}
                onClick={() => handleTabChange("orders")}
              />
              <TabButton
                label={t("map:tabs.routes")}
                isSelected={activeTab === "routes"}
                onClick={() => handleTabChange("routes")}
              />
            </ul>
          </div>
        </div>

        {/* Content */}
        <div>
          {activeTab === "orders" ? (
            <OrdersView
              filteredOrders={filteredOrders}
              selectedDate={selectedDate}
            />
          ) : (
            <DriverRoutesView drivers={drivers} selectedDate={selectedDate} />
          )}
        </div>
      </div>
    </PageLayout>
  );
};
